import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type ClassOutcome, type HistoryClass, type HistoryDay } from '../api';
import { Chip, EmptyState, ErrorState, Loading, PageIntro, useAsync } from '../components/ui';
import { formatDateTime, formatLocalDay, formatTime, localToday } from '../format';

/**
 * Training history: per day the workout and what happened to each booking,
 * reconstructed from every stored phone capture (backend cards/history.go).
 *
 * Attended means: still registered in a reading taken after the class began
 * (the reader's rule). Cancelled means: booked before, and a reading that read
 * the booking list no longer listed it, with the window between the two.
 */

const OUTCOME: Record<ClassOutcome, { label: string; kind: string }> = {
  attended: { label: 'teilgenommen', kind: 'ok' },
  cancelled: { label: 'abgemeldet', kind: 'neutral' },
  waitlisted: { label: 'Warteliste', kind: 'warn' },
  booked: { label: 'angemeldet', kind: 'accent' },
  unconfirmed: { label: 'noch nicht bestätigt', kind: 'warn' },
};

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function monthRange(ym: string): { from: string; to: string } {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function TrainingHistoryPage() {
  const [params, setParams] = useSearchParams();
  const today = localToday();
  const month = params.get('monat') ?? today.slice(0, 7);
  const [query, setQuery] = useState('');
  const { from, to } = monthRange(month);
  // The current month also shows bookings on later days of it.
  const res = useAsync(() => api.trainingHistory(from, to), [from, to]);
  const days = res.data?.days ?? [];
  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (q ? days.filter((d) => `${d.workout?.title ?? ''} ${d.workout?.body ?? ''}`.toLowerCase().includes(q)) : days),
    [days, q],
  );
  const past = days.filter((d) => d.date <= today);
  const count = (o: ClassOutcome) => past.reduce((n, d) => n + d.classes.filter((c) => c.outcome === o).length, 0);
  const [y, m] = month.split('-').map(Number);
  const setMonth = (ym: string) => {
    const next = new URLSearchParams(params);
    next.set('monat', ym);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageIntro eyebrow="CrossFit · Verlauf" title="Was du trainiert hast.">
        Workouts und Anmeldungen Tag für Tag, aus allen gespeicherten Lesungen. Teilgenommen heisst: nach Kursbeginn noch angemeldet.
      </PageIntro>
      <div className="toolbar">
        <div className="row" role="group" aria-label="Monat">
          <button type="button" className="small-button" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Vorheriger Monat">
            ‹
          </button>
          <strong aria-live="polite">
            {MONTHS[m - 1]} {y}
          </strong>
          <button type="button" className="small-button" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Nächster Monat" disabled={month >= today.slice(0, 7)}>
            ›
          </button>
        </div>
        <label className="visually-hidden" htmlFor="wod-q">
          Workouts durchsuchen
        </label>
        <input id="wod-q" type="search" placeholder="Workouts durchsuchen" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Link to="/crossfit" className="text-button">
          Zurück zu heute
        </Link>
      </div>
      {res.data && (
        <p className="muted small" role="status">
          {MONTHS[m - 1]}: {count('attended')}× teilgenommen, {count('cancelled')}× abgemeldet
          {count('unconfirmed') > 0 ? `, ${count('unconfirmed')}× noch nicht bestätigt` : ''}.
        </p>
      )}
      {res.loading && !res.data && <Loading label="Verlauf wird geladen…" />}
      {res.error && <ErrorState error={res.error} onRetry={res.reload} />}
      {res.data && shown.length === 0 && (
        <EmptyState title={q ? 'Kein Workout passt zur Suche.' : 'Keine Lesungen in diesem Monat.'}>
          <p className="muted">Der Verlauf beginnt mit der ersten gespeicherten Lesung (22. September 2026).</p>
        </EmptyState>
      )}
      <ol className="history-days">
        {shown.map((d) => (
          <DayRow key={d.date} d={d} today={today} />
        ))}
      </ol>
    </>
  );
}

function DayRow({ d, today }: { d: HistoryDay; today: string }) {
  const future = d.date > today;
  return (
    <li className={`history-day panel${future ? ' is-future' : ''}`}>
      <div className="spread">
        <h2 className="panel-title">{formatLocalDay(d.date)}</h2>
        {d.captures.length === 0 ? (
          <span className="muted small">nur aus späteren Buchungen bekannt</span>
        ) : (
          <span className="muted small">
            {d.captures.length} {d.captures.length === 1 ? 'Lesung' : 'Lesungen'}
          </span>
        )}
      </div>
      {d.classes.length === 0 ? (
        <p className="muted small">{d.bookings_read ? 'Keine Anmeldung.' : d.captures.length > 0 ? 'Anmeldungen nicht gelesen.' : ''}</p>
      ) : (
        <ul className="history-classes">
          {d.classes.map((c) => (
            <ClassRow key={c.key} c={c} />
          ))}
        </ul>
      )}
      {d.workout ? (
        <details className="disclosure">
          <summary>
            Workout: {d.workout.title || 'Workout des Tages'}
            {d.workout_captured_at && <span className="muted small"> · gelesen {formatDateTime(d.workout_captured_at)}</span>}
          </summary>
          <pre className="workout-body">{d.workout.body}</pre>
        </details>
      ) : (
        !future && d.captures.length > 0 && <p className="muted small">Workout nicht gelesen.</p>
      )}
    </li>
  );
}

function ClassRow({ c }: { c: HistoryClass }) {
  const o = OUTCOME[c.outcome];
  return (
    <li>
      <span className="history-time">
        {c.time_start}
        {c.time_end ? `–${c.time_end}` : ''}
      </span>{' '}
      {c.class_type} <Chip kind={o.kind}>{o.label}</Chip>
      {c.outcome === 'cancelled' && c.between && (
        <span className="muted small">
          {' '}
          zwischen {formatDateTime(c.between[0])} und {formatDateTime(c.between[1])}
        </span>
      )}
      {c.outcome === 'attended' && c.decided_at && <span className="muted small"> · bestätigt durch Lesung {formatTime(c.decided_at)}</span>}
      {c.outcome === 'unconfirmed' && <span className="muted small"> · seit Kursbeginn keine Lesung</span>}
    </li>
  );
}
